import React, { useCallback, useContext, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';

import t from 'react-translate';
import { AngularContext } from 'react-app';

import { useAppDispatch } from 'redux/store';
import { toggleDailyReport, toggleSftpReport, updateReportScheduling } from 'redux/actions/custom-downloads';
import { getCurrentUser } from 'redux/selectors/users';

import NvDropdown, {
  NvDropdownAlign, NvDropdownButtonStyle, NvDropdownOption,
} from 'shared/components/inputs/nv-dropdown';
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import ClickableContainer from 'components/clickable-container';
import DailySftpSwitch from './daily-sftp-switch';
import DailyEmailSwitch from './daily-email-switch';
import { DailyReportConfig } from './daily-report-configuration-modal';

type DownloadsCardOptionsProps = {
  editReport: () => void
  duplicateReport: () => void
  deleteReport: () => void
  reportId?: number
};

const DownloadsCardOptions: React.FC<DownloadsCardOptionsProps> = ({
  editReport,
  duplicateReport,
  deleteReport,
  reportId,
}) => {
  const { injectServices } = useContext(AngularContext);
  const [$state, $timeout, CustomDownloadsManager] = injectServices(['$state', '$timeout', 'CustomDownloadsManager']);
  const currentUser = useSelector((state) => getCurrentUser(state));

  const getDownloads = useCallback(() => {
    const data = CustomDownloadsManager?.data || {};
    const dataDownload = (data.dataDownload || []).map(item => ({ ...item, type: 'dataDownload' }));
    const contentDownload = (data.contentDownload || []).map(item => ({ ...item, type: 'contentDownload' }));
    return [...dataDownload, ...contentDownload];
  }, [CustomDownloadsManager?.data]);

  const downloads = getDownloads();
  const downloadInfo = downloads.find(item => item.reportId === reportId) || {};
  const { type, isDailySftp, isSentDaily, scheduledAt, recipientUserIds } = downloadInfo;
  const showSftpToggle = CustomDownloadsManager?.data?.showSftpToggle;

  const [hasDailySftpEnabled, setHasDailySftpEnabled] = useState(isDailySftp);
  const [hasDailyReprotEnabled, setHasDailyReportEnabled] = useState(isSentDaily);
  const [isTogglingSftp, setIsTogglingSftp] = useState(false);
  const [isTogglingReport, setIsTogglingReport] = useState(false);
  const [initialConfig, setInitConfig] = useState({
    scheduledAt: scheduledAt || '',
    recipientUserIds: recipientUserIds || [],
  });

  const dispatch = useAppDispatch();

  const onToggleSftpReport = () => {
    setIsTogglingSftp(true);
    dispatch(toggleSftpReport({
      catalogId: $state.params.catalogId,
      reportId,
    }))
      .then((result) => {
        if (!result.error) {
          $timeout(() => {
            CustomDownloadsManager.toggleSftpReport(reportId, !hasDailySftpEnabled);
          });
          setHasDailySftpEnabled(prev => !prev);
        }
        setIsTogglingSftp(false);
      });
  };

  const onToggleDailyReport = () => {
    setIsTogglingReport(true);
    dispatch(toggleDailyReport({
      catalogId: $state.params.catalogId,
      reportId,
    }))
      .then((result) => {
        if (!result.error) {
          $timeout(() => {
            CustomDownloadsManager.toggleDailyReport(reportId, !hasDailyReprotEnabled);
          });
          setHasDailyReportEnabled(prev => !prev);
        }
        setIsTogglingReport(false);
      });
  };

  const handleReportConfig = async (data: DailyReportConfig) => {
    const courseCustomDataReport = {
      recipientUserIds: (data.recipientUserIds || []).map(admin => admin.user.id),
      // The selected time is in user's local timezone, need to convert to UTC time for backend
      scheduledAt: moment(data.scheduledAt).utc().format('HH:mm'),
    };
    await dispatch(updateReportScheduling({
      courseCustomDataReport,
      catalogId: $state.params.catalogId,
      reportId,
    })).then((result) => {
      if (result.payload) {
        $timeout(() => {
          CustomDownloadsManager.updateCustomReport(reportId, {
            ...result.payload,
          });
        });
        setInitConfig(result.payload);
      }
    });
    return true;
  };

  let options: NvDropdownOption[];
  const deleteOption: NvDropdownOption = {
    type: 'text',
    text: t.FORM.DELETE(),
    class: 'text-danger',
    callback: deleteReport,
  };
  if (type === 'dataDownload') {
    const dataDownloadoptions: NvDropdownOption[] = [
      {
        type: 'custom',
        customItem: (
          <ClickableContainer
            className={`d-flex gray-1 px-4 py-1 align-items-center ${hasDailySftpEnabled ? 'gray-4' : ''}`}
            onClick={editReport}
            disabled={hasDailySftpEnabled}
          >
            {t.FORM.EDIT()}
            {hasDailySftpEnabled && (
              <NvTooltip text={t.CUSTOM_DOWNLOADS.SFTP_INFO()}>
                <NvIcon
                  icon='info'
                  size='xs-smallest'
                  className=' ml-1 text-primary'
                />
              </NvTooltip>
            )}
          </ClickableContainer>),
        disabled: hasDailySftpEnabled,
      },
      {
        type: 'text',
        text: t.CUSTOM_DOWNLOADS.DUPLICATE(),
        callback: duplicateReport,
        class: 'text-gray-1',
      },
      {
        type: 'divider',
      },
    ];

    if (showSftpToggle) {
      dataDownloadoptions.push(
        isTogglingSftp ? {
          type: 'text',
          text: hasDailySftpEnabled ? t.CUSTOM_DOWNLOADS.DISABLING() : t.CUSTOM_DOWNLOADS.ENABLING(),
          disabled: true,
        } : {
          type: 'custom',
          customItem: (
            <DailySftpSwitch
              isDailysftp={hasDailySftpEnabled}
              toggleSftpReport={onToggleSftpReport}
            />
          ),
        },
        {
          type: 'divider',
        },
      );
    }

    dataDownloadoptions.push(
      isTogglingReport ? {
        type: 'text',
        text: hasDailyReprotEnabled ? t.CUSTOM_DOWNLOADS.DISABLING() : t.CUSTOM_DOWNLOADS.ENABLING(),
        disabled: true,
      } : {
        type: 'custom',
        customItem: (
          <DailyEmailSwitch
            isDailyReportEnabled={hasDailyReprotEnabled}
            toggleDailyReport={onToggleDailyReport}
            onUpdateReportConfiguration={handleReportConfig}
            initialConfig={initialConfig}
          />
        ),
      },
      {
        type: 'divider',
      },
    );

    options = [...dataDownloadoptions, deleteOption];
  } else {
    options = [deleteOption];
  }

  return (
    <NvDropdown
      items={options}
      buttonStyle={NvDropdownButtonStyle.ICON}
      iconClass='icon-smallest icon-settings gray-3'
      align={NvDropdownAlign.RIGHT}
      minWidth={type === 'dataDownload' ? 250 : null}
    />
  );
};

export default DownloadsCardOptions;
